<div class="level-pane-container" 
     [class.focused]="isFocused"
     [style.--level-color]="getLevelColor()"
     (dragover)="onDragOver($event)"
     (dragleave)="onDragLeave($event)"
     (drop)="onDrop($event)">
  
  <!-- Hidden File Input -->
  <input 
    #fileInput 
    type="file" 
    multiple 
    accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.pdf"
    (change)="onFileSelected($event)"
    style="display: none;">
  
  <!-- Level Header Bar (single bar above nav+work area) -->
  <div class="level-header-bar">
    <span class="level-title">Level {{ level }}</span>
    <span class="image-count" *ngIf="currentImageIndex > 0">({{ currentImageIndex }} of {{ imageCount }})</span>
    <span class="image-count" *ngIf="currentImageIndex === 0 && imageCount > 0">({{ imageCount }} images)</span>
    <span class="image-count" *ngIf="imageCount === 0">(empty)</span>
  </div>
  
  <!-- Content Area - Two panes side by side -->
  <div class="level-content">
    <!-- Navigation Pane (Left) -->
    <div 
      #navPane
      class="nav-pane"
      [class.focused]="focusedPaneType === 'nav'"
      [style.flex]="'0 0 ' + navPaneWidth + '%'"
      (click)="paneFocused.emit('nav')"
      (keydown)="onNavPaneKeydown($event)"
      tabindex="0">
      
      <!-- Navigation Toolbar (no title) -->
      <div class="pane-header nav-header">
        <div class="pane-toolbar">
          <!-- Select All Checkbox -->
          <mat-checkbox 
            class="select-all-cb"
            [checked]="allSelected"
            [indeterminate]="someSelected && !allSelected"
            (click)="$event.stopPropagation()"
            (change)="toggleSelectAll($event.checked)"
            matTooltip="Select All">
          </mat-checkbox>
          
          <!-- Join Vertical (stacked rectangles) -->
          <button 
            mat-icon-button 
            class="toolbar-btn join-v-btn"
            (click)="$event.stopPropagation(); joinVertical()" 
            [disabled]="selectedCount < 2"
            matTooltip="Join Vertical">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="7" y="3" width="10" height="6" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <rect x="7" y="15" width="10" height="6" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M12 9 L12 15" stroke="#4CAF50" stroke-width="2"/>
              <path d="M9 12 L12 15 L15 12" fill="none" stroke="#4CAF50" stroke-width="1.5"/>
            </svg>
          </button>
          
          <!-- Join Horizontal (side-by-side rectangles) -->
          <button 
            mat-icon-button 
            class="toolbar-btn join-h-btn"
            (click)="$event.stopPropagation(); joinHorizontal()" 
            [disabled]="selectedCount < 2"
            matTooltip="Join Horizontal">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="3" y="7" width="6" height="10" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <rect x="15" y="7" width="6" height="10" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M9 12 L15 12" stroke="#4CAF50" stroke-width="2"/>
              <path d="M12 9 L15 12 L12 15" fill="none" stroke="#4CAF50" stroke-width="1.5"/>
            </svg>
          </button>
          
          <!-- Preview (eye icon) -->
          <button 
            mat-icon-button 
            class="toolbar-btn preview-btn"
            (click)="$event.stopPropagation(); previewJoin()"
            [disabled]="selectedCount < 2"
            matTooltip="Preview Join">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <ellipse cx="12" cy="12" rx="9" ry="6" fill="none" stroke="#9C27B0" stroke-width="2"/>
              <circle cx="12" cy="12" r="3" fill="#9C27B0"/>
            </svg>
          </button>
          
          <!-- Undo Join (curved arrow) -->
          <button 
            mat-icon-button 
            class="toolbar-btn undo-join-btn"
            (click)="$event.stopPropagation(); undoJoinRequested.emit()"
            matTooltip="Undo Join">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M7 10 C7 6 11 4 15 6 C19 8 19 14 15 16 L8 16" fill="none" stroke="#FF9800" stroke-width="2"/>
              <path d="M10 13 L7 16 L10 19" fill="none" stroke="#FF9800" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Delete Selected (trash icon) -->
          <button 
            mat-icon-button 
            class="toolbar-btn delete-btn"
            (click)="$event.stopPropagation(); deleteRequested.emit()" 
            [disabled]="selectedCount === 0"
            matTooltip="Delete Selected">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="6" y="7" width="12" height="14" rx="1" fill="none" stroke="#F44336" stroke-width="2"/>
              <path d="M4 7 L20 7" stroke="#F44336" stroke-width="2"/>
              <path d="M9 4 L15 4" stroke="#F44336" stroke-width="2"/>
              <path d="M10 10 L10 17" stroke="#F44336" stroke-width="1.5"/>
              <path d="M14 10 L14 17" stroke="#F44336" stroke-width="1.5"/>
            </svg>
          </button>
          
          <!-- Open Folder -->
          <button 
            mat-icon-button 
            class="toolbar-btn folder-btn"
            (click)="$event.stopPropagation(); openFileDialog()"
            matTooltip="Open Folder">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M4 6 L10 6 L12 4 L20 4 L20 18 L4 18 Z" fill="none" stroke="#2196F3" stroke-width="2"/>
              <path d="M4 10 L20 10" stroke="#2196F3" stroke-width="1.5"/>
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Thumbnails List -->
      <div class="thumbnails-container">
        <div class="drop-zone" [class.active]="isDragOver" *ngIf="imageCount === 0">
          <mat-icon>add_photo_alternate</mat-icon>
          <span>Drop images or PDF here</span>
          <span class="hint">or click folder icon to browse</span>
        </div>
        
        <div class="thumbnails-list" *ngIf="imageCount > 0">
          <app-thumbnail-item
            *ngFor="let image of levelState?.images; let i = index; trackBy: trackByImageId"
            [image]="image"
            [isSelected]="isSelected(image.id)"
            [selectionOrder]="getSelectionOrder(image.id)"
            [isActive]="currentImageId === image.id"
            [index]="i"
            (click)="onThumbnailClick(image, i)"
            (dblclick)="onThumbnailDoubleClick(image)"
            (selectionChanged)="onSelectionChange(image.id, $event)"
            (thumbnailClicked)="onThumbnailImageClick(image)"
            (filenameClicked)="onFilenameClick(image)"
            (deleteClicked)="onDeleteImage(image)"
            (excludeClicked)="onExcludeImage(image)"
            (openExternalClicked)="onOpenExternal(image)"
            (previewClicked)="onPreviewImage(image)">
          </app-thumbnail-item>
        </div>
      </div>
    </div>
    
    <!-- Work Area Pane (Right - 3x width of nav) -->
    <div 
      #workPane
      class="work-pane"
      [class.focused]="focusedPaneType === 'work'"
      [style.flex]="'1 1 auto'"
      (click)="paneFocused.emit('work')">
      
      <!-- Work Area Toolbar (no title) -->
      <div class="pane-header work-header">
        <div class="pane-toolbar">
          <!-- Mark Vertical Lines (vertical line with dots) -->
          <button 
            mat-icon-button 
            class="toolbar-btn mark-v-btn"
            [class.active]="markMode === 'vertical'"
            (click)="setMarkMode('vertical')"
            matTooltip="Mark Vertical Lines">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="12" cy="4" r="2" fill="#2196F3"/>
              <line x1="12" y1="6" x2="12" y2="18" stroke="#2196F3" stroke-width="2"/>
              <circle cx="12" cy="20" r="2" fill="#2196F3"/>
            </svg>
          </button>
          
          <!-- Mark Horizontal Lines (horizontal line with dots) -->
          <button 
            mat-icon-button 
            class="toolbar-btn mark-h-btn"
            [class.active]="markMode === 'horizontal'"
            (click)="setMarkMode('horizontal')"
            matTooltip="Mark Horizontal Lines">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="4" cy="12" r="2" fill="#9C27B0"/>
              <line x1="6" y1="12" x2="18" y2="12" stroke="#9C27B0" stroke-width="2"/>
              <circle cx="20" cy="12" r="2" fill="#9C27B0"/>
            </svg>
          </button>
          
          <!-- Cut Lines (scissors) -->
          <button 
            mat-icon-button 
            class="toolbar-btn cut-btn"
            (click)="cutLines()"
            [disabled]="!canCut"
            [matTooltip]="canCut ? 'Cut at Marked Lines' : 'Mark lines first'">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="6" cy="6" r="3" fill="none" stroke="#F44336" stroke-width="1.5"/>
              <circle cx="6" cy="18" r="3" fill="none" stroke="#F44336" stroke-width="1.5"/>
              <line x1="8" y1="8" x2="18" y2="16" stroke="#F44336" stroke-width="2"/>
              <line x1="8" y1="16" x2="18" y2="8" stroke="#F44336" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Clear Lines (X icon) -->
          <button 
            mat-icon-button 
            class="toolbar-btn clear-btn"
            (click)="clearLines()"
            [disabled]="!hasMarkedLines"
            matTooltip="Clear All Lines">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <line x1="6" y1="6" x2="18" y2="18" stroke="#FF9800" stroke-width="2.5"/>
              <line x1="18" y1="6" x2="6" y2="18" stroke="#FF9800" stroke-width="2.5"/>
            </svg>
          </button>
          
          <!-- Crop (rectangle with corners) -->
          <button 
            mat-icon-button 
            class="toolbar-btn crop-btn"
            [class.active]="cropMode"
            (click)="toggleCropMode()"
            matTooltip="Crop Image">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="6" y="6" width="12" height="12" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M3 6 L6 6 L6 3" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M21 6 L18 6 L18 3" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M3 18 L6 18 L6 21" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <path d="M21 18 L18 18 L18 21" fill="none" stroke="#4CAF50" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Undo (curved left arrow) -->
          <button 
            mat-icon-button 
            class="toolbar-btn undo-btn"
            (click)="undoRequested.emit()" 
            [disabled]="!canUndo"
            matTooltip="Undo">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M9 8 L5 12 L9 16" fill="none" stroke="#808080" stroke-width="2"/>
              <path d="M5 12 L14 12 C17 12 19 14 19 17" fill="none" stroke="#808080" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Redo (curved right arrow) -->
          <button 
            mat-icon-button 
            class="toolbar-btn redo-btn"
            (click)="redoRequested.emit()" 
            [disabled]="!canRedo"
            matTooltip="Redo">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M15 8 L19 12 L15 16" fill="none" stroke="#808080" stroke-width="2"/>
              <path d="M19 12 L10 12 C7 12 5 14 5 17" fill="none" stroke="#808080" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Hand/Pan Tool (hand shape) -->
          <button 
            mat-icon-button 
            class="toolbar-btn hand-btn"
            [class.active]="panMode"
            (click)="togglePanMode()"
            matTooltip="Hand Tool">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <path d="M12 3 L12 14 M8 5 L8 14 M16 5 L16 14 M6 9 L6 16 C6 19 8 21 12 21 C16 21 18 19 18 16 L18 8" 
                    fill="none" stroke="#808080" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          
          <!-- Zoom In (magnifier +) -->
          <button 
            mat-icon-button 
            class="toolbar-btn zoom-in-btn"
            (click)="zoomIn()"
            matTooltip="Zoom In">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="10" cy="10" r="6" fill="none" stroke="#2196F3" stroke-width="2"/>
              <line x1="14" y1="14" x2="20" y2="20" stroke="#2196F3" stroke-width="2"/>
              <line x1="7" y1="10" x2="13" y2="10" stroke="#2196F3" stroke-width="2"/>
              <line x1="10" y1="7" x2="10" y2="13" stroke="#2196F3" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Zoom Out (magnifier -) -->
          <button 
            mat-icon-button 
            class="toolbar-btn zoom-out-btn"
            (click)="zoomOut()"
            matTooltip="Zoom Out">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="10" cy="10" r="6" fill="none" stroke="#2196F3" stroke-width="2"/>
              <line x1="14" y1="14" x2="20" y2="20" stroke="#2196F3" stroke-width="2"/>
              <line x1="7" y1="10" x2="13" y2="10" stroke="#2196F3" stroke-width="2"/>
            </svg>
          </button>
          
          <!-- Reset Zoom (magnifier with reset) -->
          <button 
            mat-icon-button 
            class="toolbar-btn zoom-reset-btn"
            (click)="resetZoom()"
            matTooltip="Reset Zoom">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <circle cx="10" cy="10" r="6" fill="none" stroke="#2196F3" stroke-width="2"/>
              <line x1="14" y1="14" x2="20" y2="20" stroke="#2196F3" stroke-width="2"/>
              <path d="M7 10 A3 3 0 1 1 13 10" fill="none" stroke="#2196F3" stroke-width="1.5"/>
              <path d="M13 8 L13 11 L10 11" fill="none" stroke="#2196F3" stroke-width="1.5"/>
            </svg>
          </button>
          
          <!-- Save to Database (floppy disk) -->
          <button 
            mat-icon-button 
            class="toolbar-btn save-btn"
            (click)="saveRequested.emit()"
            [disabled]="imageCount === 0"
            matTooltip="Save to Database">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="4" y="4" width="16" height="16" rx="2" fill="none" stroke="#4CAF50" stroke-width="2"/>
              <rect x="8" y="4" width="8" height="6" fill="none" stroke="#4CAF50" stroke-width="1.5"/>
              <rect x="8" y="14" width="8" height="4" fill="#4CAF50"/>
            </svg>
          </button>
          
          <!-- Delete All (trash) - Hierarchical Delete -->
          <button 
            mat-icon-button 
            class="toolbar-btn delete-all-btn"
            (click)="deleteAllRequested.emit()"
            [disabled]="imageCount === 0"
            matTooltip="Delete All (Hierarchical - clears this level and all child levels)">
            <svg viewBox="0 0 24 24" width="18" height="18">
              <rect x="6" y="7" width="12" height="14" rx="1" fill="none" stroke="#F44336" stroke-width="2"/>
              <path d="M4 7 L20 7" stroke="#F44336" stroke-width="2"/>
              <path d="M9 4 L15 4" stroke="#F44336" stroke-width="2"/>
              <path d="M10 10 L10 17" stroke="#F44336" stroke-width="1.5"/>
              <path d="M14 10 L14 17" stroke="#F44336" stroke-width="1.5"/>
            </svg>
          </button>
        </div>
      </div>
      
      <!-- Work Area Content -->
      <div class="work-content">
        <app-image-viewer
          *ngIf="currentImage"
          [image]="currentImage"
          [cropMode]="cropMode"
          [markMode]="markMode"
          [panMode]="panMode"
          [zoomLevel]="zoomLevel"
          (cutLine)="onCutLine($event)"
          (cropRect)="onCropRect($event)"
          (lineMarked)="onLineMarked($event)"
          (debugLog)="debugLog.emit($event)">
        </app-image-viewer>
        
        <div class="empty-work-area" *ngIf="!currentImage">
          <mat-icon>image</mat-icon>
          <span>Select an image to view</span>
        </div>
      </div>
    </div>
  </div>
</div>
